/**
 * Dashboard layout persistence (server-side, per authenticated user).
 *
 * The authoritative store is the host app's `pwa-dashboard-config` API
 * (users.pwa_dashboard_config), so a user's customisation follows them across
 * devices. localStorage is kept only as a synchronous, offline-safe MIRROR of
 * the last-known config: it lets boot-time consumers (vuetify, theme) read the
 * theme instantly without flicker and keeps the dashboard usable offline.
 *
 * Source-of-truth flow:
 *   - loadLayout()  — synchronous read of the cached config (last fetched/saved)
 *   - fetchLayout() — async pull from the API; refreshes the cache, returns it
 *   - saveLayout()  — writes the cache instantly, then pushes to the API (debounced)
 *   - clearLayout() — clears the cache and resets the server config
 *
 * The tile list produced by Dashboard/List.vue stays the source of truth for
 * WHICH tiles exist; this layer only remembers ordering, the active layout tab,
 * pinned/featured tiles, and the dashboard theme. Tiles are referenced by a
 * stable id (see tileId) so modules added or removed by feature flags merge in
 * gracefully.
 */
import store from '@/store'
import VueCookie from 'vue-cookie'
import { hostAppApi } from '@/plugins/axios_settings'

/**
 * The dashboard config is per authenticated user. On the login screen there is
 * no host user (userId resolves to 0) and the token cookie may linger stale, so
 * any request would fail with a CORS/auth error and surface "Something went
 * wrong". Gate every API touch on a real token AND a non-zero host user id.
 */
function isAuthenticated () {
  return !!VueCookie.get(process.env.VUE_APP_TOKEN) && !!store.getters.userId
}

// localStorage key for the offline mirror of the server config.
const STORE_KEY = 'WelditPwa_dashboardLayout'

export const LAYOUTS = ['tiles', 'featured', 'sections', 'bento']

const DEFAULT_STATE = {
  version: 1,
  theme: 'light', // 'light' | 'dark'
  layout: 'tiles', // one of LAYOUTS
  order: [], // array of tileId, the user's preferred order
  featured: [], // array of tileId pinned to the Featured row
  wide: [], // array of tileId rendered wide in the Bento layout
  hidden: [], // array of tileId the user removed from their dashboard
  bentoDefaultsApplied: false // whether the one-time wide-tile seed has run
}

/**
 * Stable identity for a tile. Route (`to`) is unique and immutable for the
 * built-in modules; custom modules have no route, so they are identified by
 * their `slug` (unique per module — two custom modules may well point at the
 * SAME external link, so `link` alone is not an identity). Link / id / name
 * remain as fallbacks for older payloads that carry no slug.
 */
export function tileId (tile) {
  if (!tile) return ''
  return tile.to || tile.slug || tile.link || tile.id || tile.name || ''
}

/** Merge a (possibly partial / untrusted) config onto the defaults. */
function normalize (parsed) {
  const safe = parsed && typeof parsed === 'object' ? parsed : {}
  return {
    ...DEFAULT_STATE,
    ...safe,
    order: Array.isArray(safe.order) ? safe.order : [],
    featured: Array.isArray(safe.featured) ? safe.featured : [],
    wide: Array.isArray(safe.wide) ? safe.wide : [],
    hidden: Array.isArray(safe.hidden) ? safe.hidden : []
  }
}

/** In-memory cache, hydrated from the localStorage mirror at module load. */
let cache = readCache()

function readCache () {
  try {
    const raw = window.localStorage.getItem(STORE_KEY)
    return raw ? normalize(JSON.parse(raw)) : { ...DEFAULT_STATE }
  } catch (e) {
    return { ...DEFAULT_STATE }
  }
}

/** Update the in-memory cache and its localStorage mirror. */
function writeCache (state) {
  cache = normalize(state)
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(cache))
  } catch (e) { /* storage full / disabled — cache stays in memory */ }
  return { ...cache }
}

/** Resolve the per-user dashboard config endpoint on the host app. */
function endpoint () {
  return `${store.getters.getHostRefApi}pwa-dashboard-config`
}

/**
 * Synchronous read of the last-known config (cache). Used by boot-time / sync
 * consumers (vuetify, theme) that cannot await. Call fetchLayout() to refresh
 * the cache from the server.
 */
export function loadLayout () {
  return { ...cache }
}

/**
 * Pull the authoritative config from the API, refresh the cache, and return it.
 * Falls back to the cached config on any error (offline, auth, etc.) so the UI
 * never breaks when the server is unreachable.
 */
export async function fetchLayout () {
  // never call the API while unauthenticated (e.g. on the login screen)
  if (!isAuthenticated()) return { ...cache }
  try {
    const { data } = await hostAppApi.get(endpoint())
    const config = data && data.pwa_dashboard_config
    // server returns {} for a user who has not configured a dashboard yet
    if (config && typeof config === 'object' && !Array.isArray(config) && Object.keys(config).length) {
      return writeCache(config)
    }
    return { ...cache }
  } catch (e) {
    return { ...cache }
  }
}

// Debounce the API write so a burst of changes (drag reorder, repeated toggles)
// collapses into a single request. The cache is always updated synchronously.
let saveTimer = null
let pendingPayload = null

function flushSave () {
  saveTimer = null
  const payload = pendingPayload
  pendingPayload = null
  if (!payload || !isAuthenticated()) return
  hostAppApi.post(endpoint(), { pwa_dashboard_config: payload }).catch(() => {
    /* network/auth error — the cache still holds the change and will be
       re-pushed on the next save; layout is non-critical */
  })
}

/**
 * Persist the config: update the cache (and localStorage mirror) immediately so
 * the UI reflects the change at once, then push to the API debounced.
 */
export function saveLayout (state) {
  const merged = writeCache({ ...DEFAULT_STATE, ...state })
  pendingPayload = merged
  if (saveTimer) clearTimeout(saveTimer)
  saveTimer = setTimeout(flushSave, 600)
  return merged
}

/** Reset the config: clear the cache/mirror and reset the server-side config. */
export function clearLayout () {
  writeCache({ ...DEFAULT_STATE })
  try { window.localStorage.removeItem(STORE_KEY) } catch (e) { /* ignore */ }
  if (saveTimer) { clearTimeout(saveTimer); saveTimer = null }
  pendingPayload = null
  if (!isAuthenticated()) return
  hostAppApi.post(endpoint(), { pwa_dashboard_config: {} }).catch(() => { /* ignore */ })
}

/**
 * Reorder `tiles` to match the saved `order` of ids. Tiles whose id is not in
 * the saved order (newly enabled modules) keep their natural position and are
 * appended after the known ones, so a saved layout never hides a new module.
 */
export function applyOrder (tiles, order) {
  if (!Array.isArray(order) || !order.length) return tiles.slice()
  const byId = new Map(tiles.map(t => [tileId(t), t]))
  const result = []
  order.forEach(id => {
    if (byId.has(id)) {
      result.push(byId.get(id))
      byId.delete(id)
    }
  })
  // append any tiles not covered by the saved order (in their original order)
  tiles.forEach(t => { if (byId.has(tileId(t))) result.push(t) })
  return result
}
